import { useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useSearchParams } from "react-router";
import { PageSkeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { useOrg } from "@/lib/org-context";
import { AutoCreateStreamsPanel } from "./auto-create-streams-panel";
import { CredentialHandoffPanel } from "./credential-handoff-panel";
import { IntegrationsDestinationsTab } from "./integrations-destinations-tab";
import { IntegrationsSourcesTab } from "./integrations-sources-tab";
import {
  AVAILABLE_SOURCES,
  type DestinationProvider,
  type IntegrationsOutletContext,
  type SourceConnection,
  useIntegrationsOutlet,
} from "./integrations-shared";
import { OutboundWebhooksPanel } from "./outbound-webhooks-panel";

const tabs = [
  { to: "sources", label: "Sources" },
  { to: "destinations", label: "Destinations" },
  { to: "auto-create", label: "Auto-Create Streams" },
  { to: "setup-links", label: "Setup Links" },
  { to: "webhooks", label: "Outbound Webhooks" },
] as const;

export function IntegrationsPage() {
  const { orgId, canManage, organization } = useOrg();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sourceConnections, setSourceConnections] = useState<
    Record<string, SourceConnection[]>
  >({});
  const [destinations, setDestinations] = useState<DestinationProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [sourceLists, schemas] = await Promise.all([
      Promise.all(
        AVAILABLE_SOURCES.map(async (s) => {
          const res = await api<{ connections: SourceConnection[] }>(
            `/api/accounts/${orgId}/sources/${s.provider}`,
          ).catch(() => ({ connections: [] as SourceConnection[] }));
          return [s.provider, res.connections] as const;
        }),
      ),
      api<{
        providers: Array<{
          provider: string;
          status?: "ga" | "beta" | "disabled";
        }>;
      }>(`/api/providers/setup-schemas`),
    ]);
    const statusByProvider = new Map(
      (schemas.providers ?? []).map(
        (p) => [p.provider, p.status ?? "ga"] as const,
      ),
    );
    const setups = await Promise.all(
      (schemas.providers ?? []).map((p) =>
        api<DestinationProvider>(
          `/api/accounts/${orgId}/providers/${encodeURIComponent(p.provider)}/setup`,
        ),
      ),
    );
    setSourceConnections(Object.fromEntries(sourceLists));
    setDestinations(
      setups
        .filter((p) => p.kind === "destination")
        .map((p) => ({
          ...p,
          status: statusByProvider.get(p.provider) ?? "ga",
        })),
    );
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    void refresh()
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [orgId, refresh]);

  useEffect(() => {
    const zoomReturn = searchParams.get("zoom");
    const googleReturn = searchParams.get("google");
    const googleFormsReturn = searchParams.get("google_forms");
    const linkedinReturn = searchParams.get("linkedin");
    const constantContactReturn = searchParams.get("constantcontact");
    const mailchimpReturn = searchParams.get("mailchimp");
    const eventbriteReturn = searchParams.get("eventbrite");
    const microsoftTeamsReturn = searchParams.get("microsoft_teams");
    if (
      !zoomReturn &&
      !googleReturn &&
      !googleFormsReturn &&
      !linkedinReturn &&
      !constantContactReturn &&
      !mailchimpReturn &&
      !eventbriteReturn &&
      !microsoftTeamsReturn
    ) {
      return;
    }
    if (zoomReturn === "connected") {
      setMessage("Zoom is connected.");
      void refresh().catch(() => undefined);
    } else if (zoomReturn === "error") {
      setMessage("Zoom connection failed. Please try again.");
    }
    if (googleReturn === "calendar_connected") {
      setMessage(
        "Google Calendar is connected. Open the Auto-Create Streams tab to sync new Meet events automatically.",
      );
      void refresh().catch(() => undefined);
    } else if (googleReturn === "error") {
      setMessage("Google Calendar connection failed. Please try again.");
    }
    if (googleFormsReturn === "connected") {
      setMessage("Google Forms is connected.");
      void refresh().catch(() => undefined);
    } else if (googleFormsReturn === "error") {
      setMessage("Google Forms connection failed. Please try again.");
    }
    if (linkedinReturn === "connected") {
      setMessage("LinkedIn Events is connected.");
      void refresh().catch(() => undefined);
    } else if (linkedinReturn === "error") {
      setMessage("LinkedIn connection failed. Please try again.");
    }
    if (constantContactReturn === "connected") {
      setMessage("Constant Contact is connected.");
      void refresh().catch(() => undefined);
    } else if (constantContactReturn === "error") {
      setMessage("Constant Contact connection failed. Please try again.");
    }
    if (mailchimpReturn === "connected") {
      setMessage("Mailchimp is connected.");
      void refresh().catch(() => undefined);
    } else if (mailchimpReturn === "error") {
      setMessage("Mailchimp connection failed. Please try again.");
    }
    if (eventbriteReturn === "connected") {
      setMessage("Eventbrite is connected.");
      void refresh().catch(() => undefined);
    } else if (eventbriteReturn === "error") {
      setMessage("Eventbrite connection failed. Please try again.");
    }
    if (microsoftTeamsReturn === "connected") {
      setMessage("Microsoft Teams is connected.");
      void refresh().catch(() => undefined);
    } else if (microsoftTeamsReturn === "error") {
      setMessage("Microsoft Teams connection failed. Please try again.");
    }
    setSearchParams(
      (prev) => {
        if (
          !prev.has("zoom") &&
          !prev.has("reason") &&
          !prev.has("google") &&
          !prev.has("google_forms") &&
          !prev.has("error") &&
          !prev.has("linkedin") &&
          !prev.has("constantcontact") &&
          !prev.has("mailchimp") &&
          !prev.has("eventbrite") &&
          !prev.has("microsoft_teams")
        ) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        next.delete("zoom");
        next.delete("reason");
        next.delete("google");
        next.delete("google_forms");
        next.delete("error");
        next.delete("linkedin");
        next.delete("constantcontact");
        next.delete("mailchimp");
        next.delete("eventbrite");
        next.delete("microsoft_teams");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, refresh]);

  if (loading) return <PageSkeleton />;

  const outletContext: IntegrationsOutletContext = {
    orgId,
    canManage,
    organizationName: organization?.name,
    sourceConnections,
    destinations,
    message,
    setMessage,
    refresh,
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-rs-ink">Integrations</h2>
        <p className="text-sm text-rs-ink-muted">
          Organization-level sources and destinations reused across conferences.
        </p>
      </div>

      <div
        className="flex flex-wrap gap-1 border-b border-rs-border pb-2"
        role="tablist"
        aria-label="Integration sections"
      >
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={`/integrations/${tab.to}`}
            role="tab"
            className={({ isActive }) =>
              cn(
                "inline-flex h-8 items-center rounded-rs-sm px-3 text-sm font-medium text-rs-ink-muted hover:bg-rs-muted hover:text-rs-ink",
                isActive && "bg-rs-accent-soft text-rs-accent-ink",
              )
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </div>

      {message ? <p className="text-sm text-rs-danger">{message}</p> : null}

      <Outlet context={outletContext} />
    </div>
  );
}

export function IntegrationsIndexRedirect() {
  const [searchParams] = useSearchParams();
  const search = searchParams.toString();
  return <Navigate to={`sources${search ? `?${search}` : ""}`} replace />;
}

export { IntegrationsSourcesTab, IntegrationsDestinationsTab };

export function IntegrationsAutoCreateTab() {
  const { orgId, canManage, destinations, sourceConnections } =
    useIntegrationsOutlet();
  return (
    <AutoCreateStreamsPanel
      orgId={orgId}
      canManage={canManage}
      destinations={destinations.map((d) => ({
        provider: d.provider,
        label: d.label,
        configured: d.configured,
        credentialId: d.credentialId,
      }))}
      sourceConnections={Object.entries(sourceConnections).flatMap(
        ([provider, list]) =>
          list.map((c) => ({
            id: c.id,
            provider: c.provider ?? provider,
            email: c.email,
            displayName: c.displayName,
            status: c.status,
          })),
      )}
    />
  );
}

export function IntegrationsSetupLinksTab() {
  const { canManage, destinations } = useIntegrationsOutlet();
  return (
    <CredentialHandoffPanel
      canManage={canManage}
      destinations={destinations.map((d) => ({
        provider: d.provider,
        label: d.label,
        configured: d.configured,
      }))}
    />
  );
}

export function IntegrationsWebhooksTab() {
  return <OutboundWebhooksPanel />;
}
